import { useMsal } from "@azure/msal-react";
import { Warning } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFreelancerContext } from "../../../../../contexts/useFreelancerContext";
import { useFreelancerOnboardingFormSave } from "../../../../../hooks/useFreelancerOnboardingFormsSave";
import { useFreelancerOnboardingFormsUnsavedChanges } from "../../../../../hooks/useFreelancerOnboardingFormsUnsavedChanges";
import { useSessionTimeout } from "../../../../../hooks/useSessionTimeout";

const SessionTimeoutDialog: React.FC = () => {
	const { saveOnboardingFormAsync, isSaveProgressPending } =
		useFreelancerOnboardingFormSave();
	const { nextOnboardingFormStep: nextOnboardingStep } = useFreelancerContext();
	const { instance } = useMsal();
	const { t } = useTranslation();
	const { hasUnsavedChanges } = useFreelancerOnboardingFormsUnsavedChanges();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [showWarning, setShowWarning] = useState(false);

	const { remainingTime } = useSessionTimeout({
		warningTime: 5 * 60 * 1000,
		onTimeout: () => {
			instance.logout();
		},
		onWarning: () => {
			setShowWarning(true);
		}
	});

	useEffect(() => {
		if (showWarning) {
			if (hasUnsavedChanges) {
				setIsDialogOpen(true);
			}

			setShowWarning(false);
		}
	}, [showWarning, hasUnsavedChanges]);

	const formatTime = (milliseconds: number): string => {
		const totalSeconds = Math.floor(milliseconds / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes.toString().padStart(2, "0")}:${seconds
			.toString()
			.padStart(2, "0")}`;
	};

	const handleSaveAndContinue = async () => {
		try {
			await saveOnboardingFormAsync(nextOnboardingStep);
		} catch {
			// The save failed (the save hooks already showed the error snackbar): keep the
			// dialog open so the talent can retry before the session expires.
			return;
		}

		setIsDialogOpen(false);
	};

	const handleCancel = () => {
		setIsDialogOpen(false);
	};

	return (
		<Dialog
			open={isDialogOpen}
			onClose={handleCancel}
			disableEscapeKeyDown={true}
			aria-labelledby="unsaved-changes-dialog-title"
			aria-describedby="unsaved-changes-dialog-description"
			maxWidth="md"
			fullWidth
			slotProps={{
				paper: {
					sx: {
						borderRadius: 2,
						boxShadow: 3
					}
				}
			}}
		>
			<DialogTitle
				id="unsaved-changes-dialog-title"
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					pb: 1
				}}
			>
				<Warning color="warning" fontSize="large" />
				<Typography variant="h3" component="span">
					{t("idleTimeout.warning.title")}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ mt: 3 }}>
					<Typography variant="body1">{t("idleTimeout.warning.message")}</Typography>

					<Box sx={{ textAlign: "center", mt: 3 }}>
						<Typography
							variant="h2"
							color="secondary"
							sx={{ fontWeight: "bold", fontFamily: "monospace", fontSize: "3rem" }}
						>
							{formatTime(remainingTime)}
						</Typography>
					</Box>

					<Typography variant="body1" sx={{ mt: 6 }}>
						{t("idleTimeout.warning.unsavedChanges")}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
				<>
					<Button
						variant="text"
						color="secondary"
						onClick={() =>
							instance.logoutRedirect({
								postLogoutRedirectUri: "/"
							})
						}
						disabled={isSaveProgressPending}
						sx={{ minWidth: 100 }}
						disableRipple
					>
						{t("idleTimeout.actions.logout")}
					</Button>
					<Button
						variant="outlined"
						color="secondary"
						onClick={handleCancel}
						disabled={isSaveProgressPending}
						sx={{ minWidth: 140 }}
						disableRipple
					>
						{t("idleTimeout.actions.continueWithoutSaving")}
					</Button>
					<Button
						variant="contained"
						color="secondary"
						onClick={handleSaveAndContinue}
						disabled={isSaveProgressPending}
						sx={{ minWidth: 140 }}
						disableRipple
					>
						{isSaveProgressPending
							? t("idleTimeout.actions.saving")
							: t("idleTimeout.actions.saveAndContinue")}
					</Button>
				</>
			</DialogActions>
		</Dialog>
	);
};

export default SessionTimeoutDialog;
